// Shared file-picker used wherever the app needs a file from disk, so we
// don't end up with three slightly different pickers.
//
// The <input type="file"> stays a real file input the whole time - hidden
// off-screen (not display:none) so it's still keyboard/screen-reader
// reachable, with "Choose a file" as a styled click proxy. Drag-and-drop
// writes the dropped file into that same input's FileList via DataTransfer
// and fires a real "change" event, so callers don't need separate code
// paths for click vs drop.
import { el } from "./dom";
import { iconFile } from "./icons";

export interface DropzoneHandle {
  element: HTMLElement;
  fileInput: HTMLInputElement;
  setDisplay(name: string, meta: string): void;
  reset(): void;
}

export interface DropzoneOptions {
  id: string;
  accept?: string;
  emptyTitle: string;
  emptyMeta: string;
  onFileChange?: (file: File | null) => void;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function renderDropzone(opts: DropzoneOptions): DropzoneHandle {
  const attrs: Record<string, string> = { type: "file", id: opts.id, class: "visually-hidden" };
  if (opts.accept) attrs.accept = opts.accept;
  const fileInput = el("input", attrs) as HTMLInputElement;

  const fileNameEl = el("div", { class: "file-name" }, [opts.emptyTitle]);
  const fileMetaEl = el("div", { class: "file-meta" }, [opts.emptyMeta]);

  function setDisplay(name: string, meta: string): void {
    fileNameEl.textContent = name;
    fileMetaEl.textContent = meta;
  }
  function reset(): void {
    setDisplay(opts.emptyTitle, opts.emptyMeta);
  }

  const chooseBtn = el("button", { type: "button", class: "secondary-btn" }, ["Choose a file"]);
  chooseBtn.addEventListener("click", () => fileInput.click());

  const container = el("div", { class: "dropzone" }, [
    el("div", { class: "icon" }, [iconFile()]),
    el("div", { class: "file-info" }, [fileNameEl, fileMetaEl]),
    chooseBtn,
    fileInput,
  ]);

  // The input itself is invisible, so a focus ring on it wouldn't be seen -
  // move the ring to the container instead.
  fileInput.addEventListener("focus", () => container.classList.add("input-focused"));
  fileInput.addEventListener("blur", () => container.classList.remove("input-focused"));

  fileInput.addEventListener("change", () => {
    opts.onFileChange?.(fileInput.files?.[0] ?? null);
  });

  container.addEventListener("dragover", (e) => {
    e.preventDefault();
    container.classList.add("drag-over");
  });
  container.addEventListener("dragleave", () => {
    container.classList.remove("drag-over");
  });
  container.addEventListener("drop", (e) => {
    e.preventDefault();
    container.classList.remove("drag-over");
    const dropped = e.dataTransfer?.files;
    if (dropped && dropped.length > 0) {
      // Put it in the real input's FileList so every downstream consumer
      // sees the same shape of data regardless of click vs drop.
      const dt = new DataTransfer();
      dt.items.add(dropped[0]);
      fileInput.files = dt.files;
      fileInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });

  return { element: container, fileInput, setDisplay, reset };
}
